import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, MapPin, AlertTriangle, CheckCircle2, ArrowRight, Package, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';

const STATE_COMPLIANCE = [
  { code: 'AL', name: 'Alabama', minAge: 21, idRequired: true, penalty: '$100-$1,000', license: 'Required', scannerRequired: false },
  { code: 'AK', name: 'Alaska', minAge: 21, idRequired: true, penalty: '$300-$10,000', license: 'Required', scannerRequired: false },
  { code: 'AZ', name: 'Arizona', minAge: 21, idRequired: true, penalty: '$500-$2,500', license: 'Required', scannerRequired: false },
  { code: 'CA', name: 'California', minAge: 21, idRequired: true, penalty: '$400-$6,000', license: 'Required', scannerRequired: true },
  { code: 'CO', name: 'Colorado', minAge: 21, idRequired: true, penalty: '$200-$1,000', license: 'Required', scannerRequired: false },
  { code: 'CT', name: 'Connecticut', minAge: 21, idRequired: true, penalty: '$200-$1,500', license: 'Required', scannerRequired: false },
  { code: 'FL', name: 'Florida', minAge: 21, idRequired: true, penalty: '$500-$5,000', license: 'Required', scannerRequired: false },
  { code: 'GA', name: 'Georgia', minAge: 21, idRequired: true, penalty: '$100-$5,000', license: 'Not Required', scannerRequired: false },
  { code: 'IL', name: 'Illinois', minAge: 21, idRequired: true, penalty: '$200-$2,500', license: 'Required', scannerRequired: true },
  { code: 'IN', name: 'Indiana', minAge: 21, idRequired: true, penalty: '$200-$10,000', license: 'Required', scannerRequired: false },
  { code: 'MA', name: 'Massachusetts', minAge: 21, idRequired: true, penalty: '$1,000-$5,000', license: 'Required', scannerRequired: true },
  { code: 'MI', name: 'Michigan', minAge: 21, idRequired: true, penalty: '$100-$2,500', license: 'Required', scannerRequired: false },
  { code: 'NJ', name: 'New Jersey', minAge: 21, idRequired: true, penalty: '$250-$2,000', license: 'Required', scannerRequired: false },
  { code: 'NY', name: 'New York', minAge: 21, idRequired: true, penalty: '$300-$25,000', license: 'Required', scannerRequired: true },
  { code: 'NC', name: 'North Carolina', minAge: 21, idRequired: true, penalty: '$50-$500', license: 'Required', scannerRequired: false },
  { code: 'OH', name: 'Ohio', minAge: 21, idRequired: true, penalty: '$100-$1,000', license: 'Required', scannerRequired: false },
  { code: 'PA', name: 'Pennsylvania', minAge: 21, idRequired: true, penalty: '$100-$1,000', license: 'Required', scannerRequired: false },
  { code: 'TX', name: 'Texas', minAge: 21, idRequired: true, penalty: '$250-$10,000', license: 'Required', scannerRequired: false },
  { code: 'VA', name: 'Virginia', minAge: 21, idRequired: true, penalty: '$100-$2,500', license: 'Required', scannerRequired: false },
  { code: 'WA', name: 'Washington', minAge: 21, idRequired: true, penalty: '$100-$1,500', license: 'Required', scannerRequired: false },
];

export default function AgeVerificationChecker() {
  const [state, setState] = useState('');
  const selectedState = STATE_COMPLIANCE.find(s => s.code === state);

  // SEO: Set page title
  React.useEffect(() => {
    document.title = 'Tobacco Age Verification Compliance Checker by State | FlowFeed POS';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Check your state tobacco age verification requirements, FDA compliance, and penalty ranges. Free compliance quiz for smoke shops and C-stores.');
    return () => { document.title = 'FlowFeed POS | #1 Point of Sale for Smoke Shops, C-Stores & Gas Stations'; };
  }, []);

  // Quiz
  const [answers, setAnswers] = useState({});
  const questions = [
    { id: 'check_id', q: 'Do you check ID for every tobacco/vape purchase?', risk: 'high' },
    { id: 'under_30', q: 'Do you check ID for anyone who looks under 30?', risk: 'medium' },
    { id: 'training', q: 'Are all employees trained on age verification laws?', risk: 'high' },
    { id: 'signage', q: 'Do you have "We Card" or age verification signage displayed?', risk: 'low' },
    { id: 'log', q: 'Do you keep a log/record of age verifications?', risk: 'medium' },
    { id: 'scanner', q: 'Do you use an ID scanner or POS age verification system?', risk: 'medium' },
  ];
  const answered = Object.keys(answers).length;
  const noAnswers = Object.values(answers).filter(v => v === 'no').length;
  const score = answered === questions.length ? Math.round(((answered - noAnswers) / answered) * 100) : null;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <nav className="border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-emerald-600 flex items-center justify-center">
              <Package className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900 dark:text-white">FlowFeed</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/tools/tobacco-tax-calculator" className="text-sm text-slate-600 hover:text-emerald-600 hidden md:block">Tax Calculator</Link>
            <Link to="/tools/profit-margin-calculator" className="text-sm text-slate-600 hover:text-emerald-600 hidden md:block">Margin Calculator</Link>
            <Link to="/signup">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">Start Free Trial</Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-50 dark:bg-red-950 px-4 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 mb-4">
              <Shield className="h-4 w-4" /> Compliance Tool
            </motion.div>
            <h1 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              Age Verification Compliance Checker
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              Is your smoke shop compliant with tobacco age verification laws? Check your state's requirements and penalties.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            {/* State Requirements Lookup */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 md:p-8 shadow-xl mb-8"
              data-testid="compliance-checker">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-red-500" /> Your State Requirements
              </h2>
              <select value={state} onChange={(e) => setState(e.target.value)}
                className="w-full h-12 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-lg mb-4"
                data-testid="state-selector">
                <option value="">-- Select Your State --</option>
                {STATE_COMPLIANCE.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
              </select>

              {selectedState && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-red-50 dark:bg-red-950/30 p-4 rounded-xl border border-red-200 dark:border-red-800">
                      <p className="text-xs text-red-600 font-medium mb-1">Minimum Age</p>
                      <p className="text-2xl font-bold text-red-700">{selectedState.minAge}+</p>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-xl border border-amber-200 dark:border-amber-800">
                      <p className="text-xs text-amber-600 font-medium mb-1">Penalty Range</p>
                      <p className="text-lg font-bold text-amber-700">{selectedState.penalty}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <span className="text-sm text-slate-700 dark:text-slate-300">ID Check Required</span>
                      <span className="text-sm font-semibold text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4" /> Yes
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <span className="text-sm text-slate-700 dark:text-slate-300">Tobacco License</span>
                      <span className={`text-sm font-semibold ${selectedState.license === 'Required' ? 'text-red-600' : 'text-slate-500'}`}>
                        {selectedState.license}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <span className="text-sm text-slate-700 dark:text-slate-300">ID Scanner Required</span>
                      <span className={`text-sm font-semibold ${selectedState.scannerRequired ? 'text-red-600' : 'text-slate-500'}`}>
                        {selectedState.scannerRequired ? 'Yes (by law)' : 'Recommended'}
                      </span>
                    </div>
                  </div>
                  {selectedState.scannerRequired && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                      <p className="text-sm text-red-700 dark:text-red-400">
                        {selectedState.name} requires electronic ID verification. Manual visual checks alone are not sufficient for compliance.
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>

            {/* Compliance Quiz */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 md:p-8 shadow-xl mb-8"
              data-testid="compliance-quiz">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" /> Quick Compliance Check
              </h2>
              <div className="space-y-3">
                {questions.map(q => (
                  <div key={q.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 pr-4">{q.q}</span>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => setAnswers(p => ({...p, [q.id]: 'yes'}))}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          answers[q.id] === 'yes' ? 'bg-emerald-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                        }`}>Yes</button>
                      <button onClick={() => setAnswers(p => ({...p, [q.id]: 'no'}))}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          answers[q.id] === 'no' ? 'bg-red-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                        }`}>No</button>
                    </div>
                  </div>
                ))}
              </div>

              {score !== null && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className={`mt-4 p-4 rounded-xl text-center ${
                    score >= 80 ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200' :
                    score >= 50 ? 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200' :
                    'bg-red-50 dark:bg-red-950/30 border border-red-200'
                  }`}>
                  <p className={`text-2xl font-bold ${score >= 80 ? 'text-emerald-600' : score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                    {score}% Compliant
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    {score >= 80 ? 'Good! You follow best practices.' :
                     score >= 50 ? 'Needs improvement. You may be at risk of fines.' :
                     'High risk! You should address these gaps immediately.'}
                  </p>
                </motion.div>
              )}
            </motion.div>

            {/* CTA */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              className="text-center bg-gradient-to-r from-red-600 to-amber-600 rounded-2xl p-6 md:p-8 text-white">
              <h2 className="text-xl md:text-2xl font-bold mb-2">Automate Age Verification & Stay Compliant</h2>
              <p className="text-red-100 mb-4 text-sm md:text-base">
                FlowFeed POS includes built-in ID scanning, automatic age prompts for tobacco products, 
                verification logging, and compliance reports — keeping you protected from fines.
              </p>
              <Link to="/signup">
                <Button size="lg" className="bg-white text-red-700 hover:bg-red-50 font-semibold px-6">
                  Start Free 14-Day Trial <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <div className="flex items-center justify-center gap-4 mt-4 text-xs text-red-200">
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> ID Scanner</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Auto prompts</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Audit logs</span>
              </div>
            </motion.div>
          </div>

          {/* FAQ */}
          <div className="max-w-3xl mx-auto mt-16 mb-12">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 text-center">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                { q: "What is the legal age to buy tobacco in the US?", a: "Since December 2019 (Tobacco 21 law), the federal minimum age to purchase tobacco products is 21 in all US states. This includes cigarettes, cigars, vapes, e-cigarettes, and all tobacco products." },
                { q: "What happens if I sell tobacco to a minor?", a: "Penalties vary by state but typically include fines of $100-$10,000 for first offense, possible license suspension or revocation for repeat offenses, and in some states, criminal charges. FDA also conducts undercover compliance checks." },
                { q: "Do I need to check ID for every tobacco sale?", a: "Federal law requires ID verification for anyone who appears under 27 years old. However, best practice (and many state laws) recommend checking ID for everyone under 30 or even all customers regardless of appearance. Some states require electronic verification." },
                { q: "How often does the FDA check stores?", a: "The FDA conducts random compliance inspections using underage buyers. On average, stores are checked 1-2 times per year, but high-violation areas may be checked more frequently. First-time violations result in warning letters; repeat violations escalate to fines and license suspension." },
              ].map((faq, idx) => (
                <details key={idx} className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 group">
                  <summary className="px-5 py-4 cursor-pointer font-medium text-slate-900 dark:text-white list-none flex items-center justify-between">
                    {faq.q}
                    <span className="text-slate-400 group-open:rotate-45 transition-transform text-xl">+</span>
                  </summary>
                  <p className="px-5 pb-4 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 dark:border-slate-800 py-6">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">&copy; 2025 FlowFeed. Age verification compliance information for US tobacco retailers.</p>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <Link to="/" className="hover:text-emerald-600">Home</Link>
            <Link to="/tools/tobacco-tax-calculator" className="hover:text-emerald-600">Tax Calculator</Link>
            <Link to="/tools/profit-margin-calculator" className="hover:text-emerald-600">Margin Calculator</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
